import React, { useState, useEffect } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "../ui/dialog";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Label } from "../ui/label";
import { Textarea } from "../ui/textarea";
import { Switch } from "../ui/switch";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../ui/select";
import { DollarSign, Loader2 } from "lucide-react";
import { toast } from "sonner";
import TapToPayButton from "../payments/TapToPayButton";

export const PAYMENT_METHODS = [
  "Cash", "Check", "Credit Card", "Debit Card", "Zelle", "Venmo", "Bank Transfer", "Stripe", "Other",
];

const RecordPaymentDialog = ({ open, onClose, doc, isDark, onSubmit, onTapToPay }) => {
  const [amount, setAmount] = useState("");
  const [method, setMethod] = useState("Cash");
  const [date, setDate] = useState("");
  const [reference, setReference] = useState("");
  const [note, setNote] = useState("");
  const [syncJob, setSyncJob] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  const total = doc?.total || 0;
  const paid = doc?.amount_paid || 0;
  const balance = Math.max(total - paid, 0);

  useEffect(() => {
    if (open) {
      setAmount(balance > 0 ? balance.toFixed(2) : "");
      setMethod("Cash");
      setReference("");
      setNote("");
      setSyncJob(false);
      setDate(new Date().toISOString().slice(0, 10));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, doc?.invoice_id]);

  if (!doc) return null;

  const amt = parseFloat(amount) || 0;
  const willFullyPay = amt >= balance - 0.005 && balance > 0;

  const setPct = (pct) => setAmount((balance * pct).toFixed(2));

  const handleSubmit = async () => {
    if (amt <= 0) { toast.error("Enter a payment amount greater than zero"); return; }
    if (amt > balance + 0.005) { toast.error(`Amount exceeds the remaining balance ($${balance.toFixed(2)})`); return; }
    setSubmitting(true);
    try {
      await onSubmit({
        amount: amt,
        method,
        payment_date: date || undefined,
        reference: reference.trim() || undefined,
        note: note.trim() || undefined,
        mark_job_deposit_collected: syncJob,
      });
      onClose();
    } catch { /* error toast handled by parent */ }
    finally { setSubmitting(false); }
  };

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="sm:max-w-md max-h-[90vh] overflow-y-auto" data-testid="record-payment-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <DollarSign className="w-5 h-5 text-emerald-500" /> Record Payment
          </DialogTitle>
          <DialogDescription>
            {doc.invoice_number} · {doc.customer_name}
          </DialogDescription>
        </DialogHeader>

        {/* Total / Paid / Remaining summary */}
        <div className={`grid grid-cols-3 gap-2 rounded-lg p-3 text-center ${isDark ? "bg-zinc-800/50 border border-zinc-700" : "bg-gray-50 border border-gray-200"}`} data-testid="payment-balance-summary">
          <div>
            <p className="text-[10px] uppercase tracking-wider text-muted-foreground">Total</p>
            <p className="text-sm font-bold text-foreground">${total.toFixed(2)}</p>
          </div>
          <div>
            <p className="text-[10px] uppercase tracking-wider text-muted-foreground">Paid</p>
            <p className="text-sm font-bold text-emerald-500">${paid.toFixed(2)}</p>
          </div>
          <div>
            <p className="text-[10px] uppercase tracking-wider text-muted-foreground">Remaining</p>
            <p className="text-sm font-bold text-amber-500" data-testid="payment-remaining-amount">${balance.toFixed(2)}</p>
          </div>
        </div>

        <div className="space-y-4">
          {/* Apple reqs 5.1–5.3: Tap to Pay first, always visible in-app */}
          {onTapToPay && (
            <TapToPayButton
              onClick={() => onTapToPay(doc, amt > 0 ? amt : balance)}
              dataTestId="record-payment-tap-to-pay-btn"
            />
          )}

          <div className="space-y-1.5">
            <Label className="text-xs">Payment Amount *</Label>
            <Input
              type="number" step="0.01" min="0"
              value={amount}
              onChange={(e) => setAmount(e.target.value)}
              className="h-10 text-base font-semibold"
              data-testid="payment-amount-input"
            />
            <div className="flex gap-1.5 pt-0.5">
              <button type="button" onClick={() => setPct(0.25)} className="px-2 py-1 rounded-md text-[11px] font-medium bg-blue-500/10 text-blue-500 hover:bg-blue-500/20 transition-colors" data-testid="payment-pct-25">25%</button>
              <button type="button" onClick={() => setPct(0.5)} className="px-2 py-1 rounded-md text-[11px] font-medium bg-blue-500/10 text-blue-500 hover:bg-blue-500/20 transition-colors" data-testid="payment-pct-50">50%</button>
              <button type="button" onClick={() => setPct(1)} className="px-2 py-1 rounded-md text-[11px] font-medium bg-emerald-500/10 text-emerald-500 hover:bg-emerald-500/20 transition-colors" data-testid="payment-pct-full">Full Balance</button>
            </div>
            {amt > 0 && !willFullyPay && (
              <p className="text-[11px] text-amber-500" data-testid="partial-payment-hint">
                Partial payment — invoice will be marked <b>Partially Paid</b> (${Math.max(balance - amt, 0).toFixed(2)} remaining)
              </p>
            )}
            {willFullyPay && (
              <p className="text-[11px] text-emerald-500" data-testid="full-payment-hint">
                This pays the invoice in full — it will be marked <b>Paid</b>
              </p>
            )}
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-1.5">
              <Label className="text-xs">Method</Label>
              <Select value={method} onValueChange={setMethod}>
                <SelectTrigger className="h-9" data-testid="payment-method-select"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {PAYMENT_METHODS.map(m => <SelectItem key={m} value={m}>{m}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-1.5">
              <Label className="text-xs">Date</Label>
              <Input type="date" value={date} onChange={(e) => setDate(e.target.value)} className="h-9 text-sm" data-testid="payment-date-input" />
            </div>
          </div>

          <div className="space-y-1.5">
            <Label className="text-xs">Reference / Check # (optional)</Label>
            <Input value={reference} onChange={(e) => setReference(e.target.value)} placeholder="e.g. check #1042, txn id" className="h-9 text-sm" data-testid="payment-reference-input" />
          </div>

          <div className="space-y-1.5">
            <Label className="text-xs">Note (optional)</Label>
            <Textarea value={note} onChange={(e) => setNote(e.target.value)} placeholder="e.g. Deposit to order parts" rows={2} className="text-sm" data-testid="payment-note-input" />
          </div>

          {doc.job_id && !willFullyPay && (
            <div className={`flex items-center justify-between rounded-lg p-3 ${isDark ? "bg-teal-500/5 border border-teal-500/20" : "bg-teal-50 border border-teal-200"}`}>
              <div className="pr-3">
                <p className="text-sm font-medium text-foreground">Mark job as Deposit Collected</p>
                <p className="text-[11px] text-muted-foreground mt-0.5">Keeps the job active on the calendar & dispatch board</p>
              </div>
              <Switch checked={syncJob} onCheckedChange={setSyncJob} className="data-[state=checked]:bg-teal-500" data-testid="sync-job-switch" />
            </div>
          )}
        </div>

        <DialogFooter className="flex-col-reverse sm:flex-row gap-2 sm:gap-0">
          <Button type="button" variant="ghost" onClick={onClose} data-testid="record-payment-cancel">Cancel</Button>
          <Button
            onClick={handleSubmit}
            disabled={submitting || amt <= 0}
            className="bg-emerald-600 text-white hover:bg-emerald-700 gap-1.5"
            data-testid="record-payment-submit"
          >
            {submitting ? <Loader2 className="w-4 h-4 animate-spin" /> : <DollarSign className="w-4 h-4" />}
            Record ${amt > 0 ? amt.toFixed(2) : "Payment"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default RecordPaymentDialog;
